/**
 * Navigable list row.
 *
 * Used by Help & Support, Profile and the drawer — every "icon, title,
 * subtitle, chevron" row in the designs is this component.
 */

import React, { memo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { colors, radius, shadow, sizing, space } from '../../theme';
import { ChevronIcon, GlyphCircle } from './Icon';
import { Text } from './Text';

export interface ListRowProps {
  title: string;
  subtitle?: string;
  icon?: string;
  onPress?: () => void;
  /** Replaces the chevron, e.g. with a Toggle. */
  trailing?: React.ReactNode;
  /** Renders as a standalone card rather than a row inside a grouped card. */
  standalone?: boolean;
  tone?: 'default' | 'danger';
  testID?: string;
}

export const ListRow = memo(function ListRow({
  title,
  subtitle,
  icon,
  onPress,
  trailing,
  standalone = false,
  tone = 'default',
  testID,
}: ListRowProps) {
  const content = (
    <>
      {icon ? <GlyphCircle name={icon} size={40} /> : null}
      <View style={styles.body}>
        <Text variant="cardTitle" tone={tone === 'danger' ? 'danger' : 'primary'}>
          {title}
        </Text>
        {subtitle ? (
          <Text variant="caption" tone="secondary">
            {subtitle}
          </Text>
        ) : null}
      </View>
      {trailing ?? (onPress ? <ChevronIcon /> : null)}
    </>
  );

  if (!onPress) {
    return <View style={[styles.row, standalone && styles.standalone]}>{content}</View>;
  }

  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={subtitle ? `${title}. ${subtitle}` : title}
      style={({ pressed }) => [
        styles.row,
        standalone && styles.standalone,
        pressed && styles.pressed,
      ]}
    >
      {content}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    paddingVertical: space.md,
    minHeight: sizing.touchTarget,
  },
  standalone: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    paddingHorizontal: space.lg,
    ...shadow('card'),
  },
  pressed: {
    backgroundColor: colors.bg.pressed,
  },
  body: {
    flex: 1,
    gap: space.xxs,
  },
});
